<script lang="ts" setup>
import { storeToRefs } from 'pinia';
/* eslint-disable import-x/no-duplicates -- #layers/base/public is also imported for feature flag registration side effects. */
import { builtinBlocks } from 'virtual:monospace-builtin-blocks';
import { computed } from 'vue';
import { useHead } from '#app/composables/head';
import { useRoute, useRouter } from '#app/composables/router';
import { registerFilterComponents, useAuth, useLicenseExpiryToast } from '#layers/access/public';
import { useFeatureFlagKeyboardShortcuts, useUserLocale } from '#layers/base/public';
import { registerBuiltinBlocks } from '#layers/block-engine/public';
import { settingsOverlayPath } from '#layers/global-settings/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { provideSpaceRouteUpdates } from '#layers/spaces/public';
import { createSpaceRouteUpdater } from '#layers/ui-workspace/public';
import { useNuxtUiLocale } from '#layers/ui/public';
import { useAppearance } from '~/composables/use-appearance';
import { useLicenseLockRedirect } from '~/composables/use-license-lock-redirect';
import { useStudioFeatureTriggers } from '~/composables/use-studio-feature-triggers';

// Register feature flags. This import needs to remain intact.
import '#layers/base/public';
/* eslint-enable import-x/no-duplicates */

// Provide above DApp so programmatic dialogs share the route updater.
provideSpaceRouteUpdates(createSpaceRouteUpdater(useRouter()));

registerBuiltinBlocks(builtinBlocks);
registerFilterComponents();

const route = useRoute();

// The onboarding steps swoop up + blur out one after the other (out-in). Driven
// here rather than per-page so the sequence stays defined in one place; other
// layouts opt out and get no page transition.
const pageTransition = computed(() =>
	route.meta.layout === 'onboarding'
		? { name: 'onboarding-step', mode: 'out-in' as const, appear: true }
		: false,
);

// The document language drives screen readers and spellcheck, so it takes the UI language
// rather than the region-tagged format locale.
const { language } = useUserLocale();
const { currentUser } = storeToRefs(useAuth());
const nuxtUiLocale = useNuxtUiLocale();

const { translateString } = useTranslationUtils();

useHead({
	htmlAttrs: {
		lang: language,
		dir: computed(() => nuxtUiLocale.value.dir ?? 'ltr'),
	},
	// Titles built from item names can carry a `$t:` placeholder (a policy named
	// `$t:policies.studio_access`). Resolving here catches every page, including ones that
	// title themselves from data a future collection adds.
	titleTemplate: (title) => translateString(title) ?? title ?? null,
});

useAppearance(() => currentUser.value?.appearance);

useFeatureFlagKeyboardShortcuts();
useStudioFeatureTriggers();
useLicenseExpiryToast({ licensePath: settingsOverlayPath('organization/license') });
useLicenseLockRedirect();
</script>

<template>
	<NuxtRouteAnnouncer />
	<AiAppContextProvider />
	<DAppRoot>
		<AppLoading />
		<OnboardingExitCover />
		<OnboardingPopup />
		<NuxtLayout>
			<NuxtPage :transition="pageTransition" />
		</NuxtLayout>
	</DAppRoot>
</template>
